import { useEffect, useState } from "react";
import { CreditCard, Landmark, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money } from "@/lib/api";

export const PaymentMethodPicker = ({ value, onChange, testId = "payment-method-picker" }) => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/payments/methods").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return null;
  return (
    <div className="lux-card space-y-3 p-5 sm:p-6" data-testid={testId}>
      <p className="lux-eyebrow">How would you like to pay?</p>
      <div className="grid grid-cols-2 gap-2">
        {data.methods.map((m) => (
          <button key={m.key} type="button" disabled={!m.available} data-testid={`pay-method-${m.key}`} aria-pressed={value === m.key} onClick={() => onChange(m.key)}
            className={`rounded-xl border px-3 py-2.5 text-left transition-colors duration-300 disabled:cursor-not-allowed disabled:opacity-50 ${value === m.key ? "border-amber-400 bg-amber-400/10" : "border-white/10 hover:border-amber-500/40"}`}>
            <span className="block text-sm font-semibold text-amber-50">{m.label}</span>
            <span data-testid={`pay-method-${m.key}-note`} className={`block text-[11px] leading-snug ${m.available ? "text-zinc-500" : "font-semibold text-amber-500"}`}>{m.note}</span>
          </button>
        ))}
      </div>
      <p className="flex items-start gap-2 text-xs text-zinc-500" data-testid="pay-extras-note"><CreditCard className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" />{data.extras_note}</p>
    </div>
  );
};

export const payLabel = (method, total) => (method === "zelle" ? "get Zelle details" : `pay ${money(total)}`);

export const afterBooking = (data) => {
  if (data.checkout_url) { window.location.href = data.checkout_url; return; }
  if (data.zelle) toast.success("Ride saved — send your Zelle payment to dispatch it");
  else toast.success("Payment confirmed — finding your driver");
  window.dispatchEvent(new Event("lux-trip-refresh"));
};

export const ZelleDetails = ({ zelle, amount }) => (
  <div className="space-y-1.5 text-sm" data-testid="zelle-details">
    {[["Send to", zelle.name], ["Zelle email", zelle.email], ["Zelle phone", zelle.phone], ["Amount", money(amount)], ["Memo / reference", zelle.reference]].filter(([, v]) => v).map(([k, v]) => (
      <div key={k} className="flex justify-between gap-4"><span className="text-zinc-500">{k}</span><span data-testid={`zelle-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="text-right font-medium text-amber-100">{v}</span></div>
    ))}
  </div>
);

export const ZelleWaiting = ({ trip, onChange }) => {
  const [busy, setBusy] = useState(false);
  const cancel = async () => {
    setBusy(true);
    try { await api.post(`/trips/${trip.id}/cancel`); toast.success("Ride cancelled"); onChange(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="lux-card mx-auto max-w-lg space-y-5 p-6 sm:p-8" data-testid="zelle-waiting-panel">
      <Landmark className="h-8 w-8 text-amber-400" />
      <div>
        <h1 className="text-2xl font-semibold text-amber-50" data-testid="zelle-waiting-title">Waiting for Zelle payment</h1>
        <p className="mt-2 text-sm text-zinc-400">Send the amount below with Zelle and put the reference code in the memo. We'll send your driver as soon as our team confirms the payment.</p>
      </div>
      <ZelleDetails zelle={trip.zelle} amount={trip.fare.total} />
      <p className="text-xs text-zinc-500">Pickup: {trip.pickup.address}</p>
      <button data-testid="zelle-cancel-button" onClick={cancel} disabled={busy} className="btn-outline-gold w-full">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Cancel this ride</button>
    </div>
  );
};
